import React, { RefObject, useEffect, useRef, useState } from "react";
import { UIMessage } from "./Chat/Chat.types";
import {
	chatHasExportableContent,
	exportChatBundleZip,
} from "../utils/chatExcelExport";
import { exportChatToPdf } from "../utils/messageExport";
import chevronUpIcon from "../assets/chevron_up.svg";
import exportBtnImage from "../assets/export-to-pdf.svg";
import exportPdfIcon from "../assets/export-pdf.svg";
import msExcelLogo from "../assets/ms_excel_logo.svg";
import "./Dashboard/CompanyNameHeader.css";
import { friendlyErrorMessage } from "../utils/apiErrorDetail";

interface ChatExportDropdownProps {
	chatId: string | null;
	chatTitle: string;
	chatCreatedAt?: number;
	containerRef: RefObject<HTMLDivElement | null>;
	messages: UIMessage[];
	hasMoreMessages: boolean;
	isStreaming: boolean;
	isLoadingHistory: boolean;
	isLoadingAllMessages: boolean;
	onLoadAllMessages: () => Promise<void>;
}

const ChatExportDropdown: React.FC<ChatExportDropdownProps> = ({
	chatId,
	chatTitle,
	chatCreatedAt,
	containerRef,
	messages,
	hasMoreMessages,
	isStreaming,
	isLoadingHistory,
	isLoadingAllMessages,
	onLoadAllMessages,
}) => {
	type ToastVariant = "info" | "warning" | "error";
	type Toast = { text: string; variant: ToastVariant; persistent: boolean };

	const [showDropdown, setShowDropdown] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [toast, setToast] = useState<Toast | null>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const messagesRef = useRef(messages);
	messagesRef.current = messages;

	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node)
			) {
				setShowDropdown(false);
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	useEffect(() => {
		if (!toast || toast.persistent) return;
		const timer = setTimeout(() => setToast(null), 1500);
		return () => clearTimeout(timer);
	}, [toast]);

	const excelAvailable =
		chatHasExportableContent(containerRef.current, messages) || hasMoreMessages;

	const handleExport = async (type: "pdf" | "excel") => {
		if (type === "excel" && !excelAvailable) {
			setToast({
				text: "No tables or workbooks found in this chat to export",
				variant: "info",
				persistent: false,
			});
			return;
		}
		setShowDropdown(false);
		setIsExporting(true);
		try {
			if (hasMoreMessages) {
				await onLoadAllMessages();
			}
			await new Promise((resolve) => requestAnimationFrame(resolve));

			const currentMessages = messagesRef.current;

			if (type === "pdf") {
				await exportChatToPdf(
					containerRef.current!,
					chatId!,
					chatTitle,
					chatCreatedAt,
					currentMessages,
				);
			} else {
				if (!chatHasExportableContent(containerRef.current, currentMessages)) {
					setToast({
						text: "No tables or workbooks found in this chat",
						variant: "info",
						persistent: false,
					});
					return;
				}
				const result = await exportChatBundleZip(
					containerRef.current,
					chatId!,
					chatTitle,
					currentMessages,
				);
				if (result.skippedWorkbooks > 0) {
					// Partial export: zip downloaded but at least one workbook
					// fetch failed (most often a CORS/expired-URL issue).  Stay
					// loose — the user keeps what we got — but make the warning
					// persistent and red so they actually notice and can re-run.
					setToast({
						text: `Export incomplete: ${result.skippedWorkbooks} workbook${
							result.skippedWorkbooks === 1 ? "" : "s"
						} could not be downloaded. Try exporting again.`,
						variant: "error",
						persistent: true,
					});
				}
			}
		} catch (err) {
			console.error(`Chat ${type} export failed:`, err);
			// The excel path raises its own readable reasons (a workbook that
			// could not be fetched); everything else here is a thrown library
			// error, which is why this goes through the friendly mapping.
			const detail = friendlyErrorMessage(
				type === "excel" ? err : undefined,
				`${type === "pdf" ? "PDF" : "Excel"} export failed. Please try again.`,
			);
			setToast({ text: detail, variant: "error", persistent: true });
		} finally {
			setIsExporting(false);
		}
	};

	const isDisabled =
		isStreaming ||
		isLoadingHistory ||
		isLoadingAllMessages ||
		!chatId ||
		isExporting;

	if (messages.length === 0) return null;

	return (
		<div
			ref={dropdownRef}
			style={{ position: "relative", display: "flex", alignItems: "center" }}
		>
			<button
				onClick={() => setShowDropdown((prev) => !prev)}
				disabled={isDisabled}
				className="export-button"
				style={isDisabled && !isExporting ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
			>
				{isExporting ? (
					<>
						<svg
							width="16"
							height="16"
							viewBox="0 0 16 16"
							style={{ animation: "spin 1s linear infinite" }}
						>
							<circle
								cx="8"
								cy="8"
								r="6"
								fill="none"
								stroke="#d1d5db"
								strokeWidth="2"
							/>
							<path
								d="M8 2a6 6 0 0 1 6 6"
								fill="none"
								stroke="#008782"
								strokeWidth="2"
								strokeLinecap="round"
							/>
						</svg>
						<span className="export-text">Exporting…</span>
					</>
				) : (
					<>
						<img
							src={exportBtnImage}
							alt="Export button"
							className="export-icon"
						/>
						<span className="export-text">Export</span>
						<img
							src={chevronUpIcon}
							alt="Toggle"
							height="16px"
							width="16px"
							className={`export-chevron ${showDropdown ? "" : "collapsed"}`}
						/>
					</>
				)}
			</button>

			{showDropdown && (
				<div className="export-dropdown-menu">
					<div
								className="export-dropdown-item"
								onClick={() => handleExport("excel")}
								title={!excelAvailable ? "No tables or workbooks found in this chat" : undefined}
								style={!excelAvailable ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
							>
								<div className="export-item-icon-container">
									<img
										src={msExcelLogo}
										alt="Excel"
										className="export-item-icon"
									/>
								</div>
								<span className="export-item-text">Export as Excel</span>
							</div>

							<div className="export-dropdown-divider" />

					<div
						className="export-dropdown-item"
						onClick={() => handleExport("pdf")}
					>
						<div className="export-item-icon-container">
							<img
								src={exportPdfIcon}
								alt="PDF"
								className="export-item-icon"
							/>
						</div>
						<span className="export-item-text">Export as PDF</span>
					</div>
				</div>
			)}

			{toast && (
				<div
					className="export-dropdown-menu"
					style={{
						width: "max-content",
						maxWidth: 320,
						padding: "8px 12px",
						fontSize: 13,
						display: "flex",
						alignItems: "flex-start",
						gap: 8,
						...(toast.variant === "error"
							? {
									color: "#dc2626",
									backgroundColor: "#fef2f2",
									borderColor: "#fecaca",
								}
							: toast.variant === "warning"
								? {
										color: "#92400e",
										backgroundColor: "#fffbeb",
										borderColor: "#fde68a",
									}
								: {
										color: "#6b7280",
										backgroundColor: "#f9fafb",
										borderColor: "#e5e7eb",
									}),
					}}
				>
					<span style={{ flex: 1 }}>{toast.text}</span>
					{toast.persistent && (
						<button
							onClick={() => setToast(null)}
							aria-label="Dismiss"
							style={{
								background: "transparent",
								border: "none",
								color: "inherit",
								cursor: "pointer",
								padding: 0,
								lineHeight: 1,
								fontSize: 16,
								opacity: 0.6,
							}}
						>
							×
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export default ChatExportDropdown;
